import React from 'react';
import { MONAMI_WWW_HOST } from '../config/env';

interface AuthLayoutProps {
  children: React.ReactNode;
  /** Extra classes on the outer wrapper (e.g. z-50 for overlays) */
  className?: string;
  /** Max width of the white card. Defaults to 740px. */
  maxWidth?: string;
}

/**
 * Shared full-screen layout for auth-related screens (login, lock, biometric setup).
 * Dark teal background, centered white card with logo + GO badge, and legal footer.
 */
export default function AuthLayout({
  children,
  className = '',
  maxWidth = 'max-w-[740px]',
}: AuthLayoutProps) {
  return (
    <div className={`absolute inset-0 flex flex-col bg-dark-teal ${className}`}>
      <main className="flex flex-1 overflow-y-auto">
        <div className="flex min-h-full w-full items-center justify-center px-4 py-8">
          <div
            className={`w-full ${maxWidth} rounded bg-white p-8 sm:p-12 shadow-lg flex flex-col gap-9`}
          >
            {/* Logo + GO badge */}
            <div className="flex items-center gap-4">
              <img
                src="/ma-logo-full.svg"
                alt="Mon Ami"
                className="h-10 w-auto"
              />
              <span className="text-[10px] font-semibold uppercase tracking-[5px] text-gray-900">
                GO
              </span>
            </div>

            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white px-6 py-4 sm:px-[120px]">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <a
              href="mailto:support@monami.io"
              className="text-sm text-deep-sea-green hover:underline"
            >
              support@monami.io
            </a>
          </div>

          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-[#555]">
              <a
                href={`${MONAMI_WWW_HOST}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                Privacy Policy
              </a>
              <a
                href={`${MONAMI_WWW_HOST}/terms-of-use`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                Terms of Use
              </a>
            </div>
            <span className="font-medium text-deep-sea-green">© Mon Ami</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
